import React, { useCallback } from 'react';
import { Align, PanelToggler } from './PanelToggler';
import './DesignerPanel.less';
import { ISplitterDelta, Splitter } from 'components/layout/Splitter';
import classNames from 'classnames';
import { ITab } from 'components/tabs/Tabs';
import { ValidationEntry } from 'components/HelpCenter/ValidationEntry';
import { HelpCenter } from 'components/HelpCenter/HelpCenter';
import { HelpCenterLocation } from 'components/HelpCenter/HelpCenterLocation';

export interface IDesignerPanelProps {
  align: Align;
  tabs: ITab[];
  width: number;
  minWidth?: number;
  maxWidth?: number;
  currentTabId?: string | null;
  validations?: ValidationEntry[];
  onTabSelected?: (name: string | null) => void;
  onWidthChanged?: (newWidth: number) => void;
}

function isValidCurrentTab(tabs: ITab[], currentTabId: string | null | undefined) {
  return currentTabId != null && tabs.find(item => item.value === currentTabId) != null;
}

function isDisabledPanel(tabs: ITab[], panelValue: string) {
  const panel = tabs.find(item => item.value === panelValue);
  return panel?.disabled === true;
}

export function DesignerPanel({
  align,
  tabs,
  width,
  minWidth,
  maxWidth,
  currentTabId: currentTabIdProp,
  validations,
  children,
  onTabSelected,
  onWidthChanged,
}: React.PropsWithChildren<IDesignerPanelProps>) {
  const handleSelectedTab = useCallback(
    (tab: string | null) => {
      onTabSelected?.(tab);
    },
    [onTabSelected]
  );

  const handleDragEnd = useCallback(
    (position: ISplitterDelta) => {
      const deltaX = align === Align.left ? position.deltaX : -1 * position.deltaX;
      let newWidth = width + deltaX;

      if (minWidth != null) {
        newWidth = Math.max(newWidth, minWidth);
      }

      if (maxWidth != null) {
        newWidth = Math.min(newWidth, maxWidth);
      }

      onWidthChanged?.(newWidth);
    },
    [align, maxWidth, minWidth, onWidthChanged, width]
  );

  const selectedTabId =
    currentTabIdProp != null && isValidCurrentTab(tabs, currentTabIdProp) && !isDisabledPanel(tabs, currentTabIdProp)
      ? currentTabIdProp
      : null;

  const renderTabs = () => (
    <PanelToggler tabs={tabs} selectedTab={selectedTabId} onTabSelected={handleSelectedTab} align={align} />
  );

  const renderSplitter = () => (
    <Splitter orientation="vertical" onDragEnd={handleDragEnd} className={'dpc-designer-panel__splitter'} />
  );

  return (
    <div
      className={classNames('dpc-designer-panel', {
        'dpc-designer-panel--left': align === Align.left,
        'dpc-designer-panel--right': align === Align.right,
        'dpc-designer-panel--closed': selectedTabId == null,
        'dpc-designer-panel--empty': tabs.length === 0,
      })}
    >
      {align === Align.left && (
        <div className={'dpc-designer-panel__nav'}>
          {renderTabs()}
          <HelpCenter entries={validations} newStyle location={HelpCenterLocation.DataDesigner} />
        </div>
      )}
      {align === Align.right && renderSplitter()}
      <div style={{ width, minWidth, maxWidth }} className="dpc-designer-panel__content">
        {children}
      </div>
      {align === Align.left && renderSplitter()}
      {align === Align.right && renderTabs()}
    </div>
  );
}
